import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { getDatesFromSessions, filterAvailableSessions } from 'utils/session';
import { formatEventDateShort } from 'utils/dateTime';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { SkeletonLineSlim } from 'components/theme/Skeleton/Skeleton';
import useWaitList from 'api/waitlist/useWaitList';
import getUpdatedSelectedDate from 'utils/data/getUpdatedSelectedData';
import getFirstAvailableDateFromSessions from 'utils/data/firstAvailableDate/getFirstAvailableDateFromSessions';
import ButtonsOptionsList, { ButtonsOptionSizes } from './ButtonsOptionsList';

const TicketDateSelector = () => {
  const { locale } = useGlobalDateLocale();
  const {
    isLoading,
    listing,
    eventSessions,
    date,
    setEventBookingSessionParams,
  } = useEventBookingSession();
  const { data: waitlist } = useWaitList(listing?.waitlistId, listing?.brandId);
  const firstAvailableScheduledDate =
    getFirstAvailableDateFromSessions(eventSessions)?.startDateTime?.split(
      'T',
    )[0];
  const selectedDate = getUpdatedSelectedDate(
    date,
    firstAvailableScheduledDate,
    waitlist,
  );

  const availableDates = useMemo(() => {
    const availableSessions = filterAvailableSessions(eventSessions);
    return getDatesFromSessions(availableSessions).map((date) => ({
      value: date,
      label: formatEventDateShort(date, locale),
    }));
  }, [eventSessions, locale]);

  const handleClick = useCallback(
    (date: string) => {
      if (date !== selectedDate) {
        setEventBookingSessionParams({ date });
      }
    },
    [selectedDate, setEventBookingSessionParams],
  );

  if (isLoading || !listing || !selectedDate) {
    return (
      <Wrapper>
        <DateButtonSkeleton />
        <DateButtonSkeleton />
      </Wrapper>
    );
  }

  return (
    <ButtonsOptionsList
      options={availableDates}
      selectedValue={selectedDate}
      onOptionClick={handleClick}
      buttonSize={ButtonsOptionSizes.BIG}
    />
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 12px;
`;

const DateButtonSkeleton = styled(SkeletonLineSlim)`
  width: 128px;
  height: 45px;
`;

export default TicketDateSelector;
